import { Stack, Typography } from '@mui/material';
import { capitalize, every, find, mapValues } from 'lodash';
import { useMemo } from 'react';

import { useDevices } from '@/api/devices';
import { InvoiceLineItem } from '@/api/invoices';
import { telemetryTypeToLabelMap } from '@/api/subscriptionTags';
import { SubscriptionOptionItem, useSubscriptionsItems } from '@/api/subscriptions';
import { DisplayCurrency } from '@/components/DisplayCurrency';
import { Timestamp } from '@/components/data/Timestamp';
import { GenericSkeleton } from '@/components/loaders/GenericSkeleton';
import { TotalDueReadout, TransactionSummary } from '@/components/transactions/TransactionModal';
import { InlineAlert, InlineAlertBody } from '@/components/ui/InlineAlert';
import { SummaryCard, SummaryCardBody, SummaryCardRow } from '@/components/ui/SummaryCard';
import { useTransactionContext } from '@/context/transaction';
import {
    LineType,
    findByTelemetryLinePriceLookupKey,
    useTelemetryLineChanges,
} from '@/context/transaction/TelemetryLineChangeContext';
import { itemQuantity } from '@/utils/languageHelpers';

export const TelemetryTransactionSummary = () => {
    const { data: subsItems } = useSubscriptionsItems();
    const { billingDate, proratedLineItems, isCancellation, isNewSubscription, isPastDue } =
        useTransactionContext('subscription');
    const { affectedSpotters } = useTelemetryLineChanges();
    const { data: devices } = useDevices({
        devices: Object.values(affectedSpotters).flatMap((ids) => ids.map((spotterId) => ({ spotterId }))),
    });
    const deviceLabels = useMemo(
        () =>
            mapValues(
                affectedSpotters,
                (ids) => ids.map((spotterId) => find(devices, { spotterId })?.name || spotterId) // || since sometimes it's empty
            ),
        [devices, affectedSpotters]
    );

    const cellularSubscriptionItem = findByTelemetryLinePriceLookupKey(subsItems?.data, 'cellular', 'month');
    const iridiumSubscriptionItem = findByTelemetryLinePriceLookupKey(subsItems?.data, 'iridium', 'month');

    // Find corresponding upcoming invoice line items
    const cellularLineItems = proratedLineItems?.filter((line) => {
        return line.price_id === cellularSubscriptionItem?.price_id;
    });
    const iridiumLineItems = proratedLineItems?.filter((line) => {
        return line.price_id === iridiumSubscriptionItem?.price_id;
    });

    return (
        <Stack sx={{ gap: 3 }}>
            {isNewSubscription && (
                <InlineAlert title="By enabling telemetry, you will automatically be charged monthly" severity="info">
                    <InlineAlertBody>
                        You will be charged automatically at the beginning of every invoice period as part of your
                        telemetry subscription. Your payment method will automatically be charged with your next invoice
                        on <Timestamp value={billingDate} unit="date" />.
                    </InlineAlertBody>
                </InlineAlert>
            )}
            {isCancellation && (
                <InlineAlert title="You are about to cancel your recurring subscription" severity="error">
                    <InlineAlertBody>
                        All lines will be immediately deactivated and all devices will lose telemetry service when you
                        confirm. You can resume service at any time by re-enabling device telemetry.
                    </InlineAlertBody>
                </InlineAlert>
            )}
            <TransactionSummary>
                <TelemetryTransactionLineItemsBlock
                    type="cellular"
                    spotterLabels={deviceLabels.cellular}
                    subscriptionItem={cellularSubscriptionItem}
                    lineItems={cellularLineItems}
                    billingDate={billingDate}
                    isNewSubscription={isNewSubscription}
                    isPastDue={isPastDue}
                />
                <TelemetryTransactionLineItemsBlock
                    type="iridium"
                    spotterLabels={deviceLabels.iridium}
                    subscriptionItem={iridiumSubscriptionItem}
                    lineItems={iridiumLineItems}
                    billingDate={billingDate}
                    isNewSubscription={isNewSubscription}
                    isPastDue={isPastDue}
                />
                {!isPastDue && <TotalDueReadout />}
            </TransactionSummary>
        </Stack>
    );
};

type TelemetryTransactionLineItemsBlockProps = {
    type: LineType;
    spotterLabels: string[];
    subscriptionItem?: SubscriptionOptionItem;
    lineItems?: InvoiceLineItem[];
    billingDate?: Date;
    isNewSubscription: boolean;
    isPastDue: boolean;
};

const TelemetryTransactionLineItemsBlock = ({
    type,
    spotterLabels,
    subscriptionItem,
    lineItems,
    billingDate,
    isNewSubscription,
    isPastDue,
}: TelemetryTransactionLineItemsBlockProps) => {
    if (spotterLabels.length === 0 && lineItems && every(lineItems, ({ quantity }) => quantity === 0)) return false;

    if (!subscriptionItem || !lineItems) {
        return <GenericSkeleton lines={3} />;
    }

    const telemetryLabel = telemetryTypeToLabelMap[type];

    return (
        <Stack sx={{ gap: 3 }} data-testid={`telemetry-transaction-${type}-changes-box`}>
            <Typography variant="h5" gutterBottom>
                {capitalize(telemetryLabel)} line changes
            </Typography>
            <SummaryCardRow>
                <SummaryCard title={`Affected devices (${spotterLabels.length})`}>
                    <SummaryCardBody>
                        {spotterLabels.join(', ')}
                        {!spotterLabels.length && <em>None</em>}
                    </SummaryCardBody>
                </SummaryCard>
                <SummaryCard title={`Base monthly ${telemetryLabel} price`}>
                    <SummaryCardBody>
                        <DisplayCurrency
                            variant="inherit"
                            sx={{ display: 'inline-block' }}
                            value={subscriptionItem.unit_amount}
                            currency={subscriptionItem.currency}
                            hideZeroCents
                            cents
                        />
                        / {subscriptionItem.interval}
                    </SummaryCardBody>
                </SummaryCard>
            </SummaryCardRow>
            <Stack sx={{ gap: 3 }}>
                {lineItems.map((lineItem) =>
                    isNewSubscription ? (
                        <NewSubscriptionTelemetryLineItem key={lineItem.id} type={type} lineItem={lineItem} />
                    ) : (
                        <ProratedTelemetryLineItem
                            key={lineItem.id}
                            type={type}
                            currentPeriodEnd={billingDate}
                            lineItem={lineItem}
                        />
                    )
                )}
                {lineItems.length === 0 && !isPastDue && (
                    <NoChangesToTelemetryLineItem type={type} priceId={subscriptionItem.price_id} />
                )}
            </Stack>
        </Stack>
    );
};

type ProratedTelemetryLineItemProps = {
    type: LineType;
    lineItem: InvoiceLineItem;
    currentPeriodEnd?: Date;
};

const ProratedTelemetryLineItem = ({ lineItem, type, currentPeriodEnd }: ProratedTelemetryLineItemProps) => {
    if (lineItem.amount === 0) {
        // prorated line items of 0 are always just Stripe noise
        return false;
    }

    const isRefund = lineItem.amount < 0;
    return (
        <Stack data-testid="telemetry-summary-line-item">
            <Stack direction="row" gap={2} flex={1} justifyContent="space-between">
                <Typography variant="h5">
                    {isRefund ? (
                        <>
                            Prorated refund for current {telemetryTypeToLabelMap[type]} subscription (
                            {itemQuantity(lineItem.quantity, 'line')})
                        </>
                    ) : (
                        <>
                            Prorated cost for new {telemetryTypeToLabelMap[type]} subscription (
                            {itemQuantity(lineItem.quantity, 'line')})
                        </>
                    )}
                </Typography>
                <DisplayCurrency variant="body1" value={lineItem.amount} currency={lineItem.currency} cents />
            </Stack>
            <Typography variant="body2">
                Based on {isRefund ? 'unused' : 'remaining'} days in invoice period ending on{' '}
                <Timestamp value={currentPeriodEnd} unit="date" type="medium" />
            </Typography>
        </Stack>
    );
};

const NewSubscriptionTelemetryLineItem = ({ lineItem, type }: ProratedTelemetryLineItemProps) => {
    return (
        <Stack
            direction="row"
            gap={2}
            flex={1}
            justifyContent="space-between"
            data-testid="telemetry-summary-line-item"
        >
            <Typography variant="h5">
                Cost for new {telemetryTypeToLabelMap[type]} subscription ({itemQuantity(lineItem.quantity, 'line')})
            </Typography>
            <DisplayCurrency variant="body1" value={lineItem.amount} currency={lineItem.currency} cents />
        </Stack>
    );
};

/**
 * Dummy variant of ProratedTelemetryLineItem for when we need a placeholder saying this line item didn't change
 * (but we still want to show affected spotters)
 */
const NoChangesToTelemetryLineItem = ({ type, priceId }: { type: LineType; priceId: string }) => {
    const { lineItemQuantities } = useTransactionContext('subscription');
    return (
        <Stack
            direction="row"
            gap={2}
            flex={1}
            justifyContent="space-between"
            data-testid="telemetry-summary-line-item"
        >
            <Typography variant="h5">
                No change to current {telemetryTypeToLabelMap[type]} line subscription (
                {itemQuantity(lineItemQuantities[priceId] ?? 0, 'line')})
            </Typography>
        </Stack>
    );
};
